import { CoaTemplateApplySource } from "@common/types/domains/accounting/chartOfAccountsTemplateApplyEvent";
import type { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { canRoleEditCoa } from "@hooks/permissions/canRoleEditCoa";
import {
    isChangeSetEmpty,
    type CoaTemplateActivityEventResult,
} from "./coaTemplateActivityEvents";

// Why a rail control is greyed out. These mirror `isCheckboxCapableEvent`: every
// condition that makes an entity unselectable gets copy, so a disabled control is
// never just "dimmed with no explanation".
export const UNDO_DISABLED_REASONS = {
    loadFailed: "Couldn’t load this entity’s undo details",
    noEditAccess:
        "You need Chart of Accounts edit access in this client to undo",
    notWizardApply: "Only applies from the template wizard can be undone",
    emptyChangeSet: "This apply changed nothing to undo",
} as const;

export function buildUndoDisabledReasons({
    events,
    workspaceRolesById,
}: {
    events: CoaTemplateActivityEventResult[];
    workspaceRolesById: ReadonlyMap<string, UserWorkspaceRole>;
}): Map<number, string> {
    const reasons = new Map<number, string>();
    for (const event of events) {
        const reason = getEventUndoDisabledReason({
            event,
            workspaceRole: workspaceRolesById.get(event.eventRef.workspaceId),
        });
        if (reason) {
            reasons.set(event.eventRef.entityId, reason);
        }
    }
    return reasons;
}

function getEventUndoDisabledReason({
    event,
    workspaceRole,
}: {
    event: CoaTemplateActivityEventResult;
    workspaceRole: UserWorkspaceRole | undefined;
}): string | undefined {
    if (event.status === "error") {
        return UNDO_DISABLED_REASONS.loadFailed;
    }
    // A reverted entity shows the "Reverted" state instead of a control, so it
    // has nothing to explain.
    if (event.detail.revertedAt !== null) {
        return undefined;
    }
    if (!workspaceRole || !canRoleEditCoa(workspaceRole)) {
        return UNDO_DISABLED_REASONS.noEditAccess;
    }
    if (event.detail.source !== CoaTemplateApplySource.WIZARD) {
        return UNDO_DISABLED_REASONS.notWizardApply;
    }
    if (isChangeSetEmpty(event.detail)) {
        return UNDO_DISABLED_REASONS.emptyChangeSet;
    }
    return undefined;
}
